// Recover a tab that outlived a deploy.
//
// Vite builds hashed chunks (`assets/web-BpodS5JD.js`) and the dashboard image
// serves only the chunks of the build it was made from, so a redeploy makes the
// previous hashes 404. A tab opened before the deploy still runs the old JS: the
// moment it lazy-loads a route or a plugin, the `import()` asks for a filename
// the server no longer has and throws `Failed to fetch dynamically imported
// module`. Seen in prod as a silent failure to register web push - the settings
// screen simply never got its device token.
//
// Vite fires `vite:preloadError` on window for exactly this case, INCLUDING when
// the importing code catches the rejection itself. Reloading pulls a fresh
// index.html, which points at the hashes that do exist.

import { Capacitor } from '@capacitor/core';
import { reportStaleChunk } from '@/lib/observability/faro-client';

const RELOADED_KEY = 'restaurant:stale-chunk-reloaded';

/** sessionStorage can throw (Safari private mode, embedded webviews). A tab that
 *  can't remember it already reloaded must never reload again — that is the
 *  reload loop this guard exists to prevent — so failures read as "reloaded". */
function alreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOADED_KEY) === '1';
  } catch {
    return true;
  }
}

function markReloaded(): boolean {
  try {
    sessionStorage.setItem(RELOADED_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Reload once per tab when a chunk fails to load. Install before anything lazy
 * is imported (i.e. at the top of the entry).
 *
 * Deliberately at most ONE reload per session: if the import failed for any
 * reason other than a deploy — the network is down, the server is broken — the
 * reload fails too, and an unguarded handler would put the tab in a refresh
 * loop. One attempt fixes the deploy case and leaves every other case exactly as
 * it is today.
 */
export function installStaleChunkReload(): void {
  if (typeof window === 'undefined') return;
  // Native ships its assets inside the bundle, so there is no hash to go stale.
  // A preload failure there means a genuinely broken bundle, which a reload
  // cannot fix — and OTA swaps are the updater's job (lib/services/app-updater-service).
  if (Capacitor.isNativePlatform()) return;

  window.addEventListener('vite:preloadError', (event) => {
    const message =
      event instanceof CustomEvent && event.detail instanceof Error
        ? event.detail.message
        : ((event as Event & { payload?: Error }).payload?.message ?? 'unknown import error');

    // Offline: the fetch failed because there is no network, not because the
    // asset is gone. Reloading would land on the browser's offline page and
    // burn the one reload this tab gets.
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (offline || alreadyReloaded() || !markReloaded()) {
      reportStaleChunk(message, false);
      return;
    }

    // We're handling it — stop Vite from rethrowing into the caller (and into
    // Faro) for the fraction of a second before the reload takes effect.
    event.preventDefault();
    reportStaleChunk(message, true);
    window.location.reload();
  });
}
